// One component, 15 variants (contract section 27, CONTRACT.md "States").
// Renders as a pill (inline: rows, headers, tabs) or a block (a region:
// dashed frame, title, reason line, next actions as small buttons). It is
// inline in the region it describes. reason and nextAction are required; in dev a
// missing or blank value logs a lint-style warning so it cannot ship silently.

import type { MouseEvent, ReactNode } from 'react';
import { Button, ButtonLink } from '../ui/Button';
import { HONEST_STATE_VARIANTS, toneOf, type HonestStateVariant } from './variants';
import './HonestState.css';

export type { HonestStateVariant } from './variants';
export { HONEST_STATE_VARIANTS } from './variants';

export interface HonestAction {
  readonly label: string;
  // One of the two: a click handler for in-app actions, an href for links out.
  readonly onSelect?: () => void;
  readonly href?: string;
  // Key cap hint printed after the label, for example "w".
  readonly keyHint?: string;
}

export interface HonestStateProps {
  readonly variant: HonestStateVariant;
  // Specific, human sentence: what was observed and why the state applies.
  readonly reason: string;
  // What the reader can do next. The pill keeps it in the accessible name;
  // the block renders it as buttons or links.
  readonly nextAction: HonestAction | readonly HonestAction[];
  readonly as?: 'pill' | 'block';
  // Block title. Defaults to the variant label.
  readonly title?: string;
  // Extra line under the reason (a formula, a cutoff, an elapsed time).
  readonly detail?: ReactNode;
  readonly className?: string;
  readonly id?: string;
}

function toActions(nextAction: HonestAction | readonly HonestAction[] | null | undefined): readonly HonestAction[] {
  const candidates: readonly unknown[] = Array.isArray(nextAction) ? nextAction : [nextAction];
  return candidates.filter((action): action is HonestAction => typeof action === 'object' && action !== null);
}

function warnInDev(message: string): void {
  if (import.meta.env.DEV) {
    console.warn(`[HonestState] ${message}`);
  }
}

// Lint-like runtime check, dev only. The TypeScript types already require the
// props; this catches empty strings and empty arrays that types cannot.
export function validateHonestStateProps(props: Pick<HonestStateProps, 'variant' | 'reason' | 'nextAction'>): readonly string[] {
  const problems: string[] = [];
  if (!(HONEST_STATE_VARIANTS as readonly string[]).includes(props.variant)) {
    problems.push(`unknown variant "${String(props.variant)}"`);
  }
  if (typeof props.reason !== 'string' || props.reason.trim() === '') {
    problems.push(`variant "${props.variant}" rendered without a reason`);
  }
  const actions = props.nextAction === undefined ? [] : toActions(props.nextAction);
  if (actions.length === 0) {
    problems.push(`variant "${props.variant}" rendered without a next action`);
  }
  for (const action of actions) {
    if (!action.label?.trim()) problems.push(`variant "${props.variant}" has an action without a label`);
    if (!action.onSelect && !action.href) problems.push(`variant "${props.variant}" action "${action.label}" has neither onSelect nor href`);
  }
  return problems;
}

// An href that names a scheme leaves the product (DEX Screener, Axiom, an X
// profile) and opens in its own tab. An href that does not is one of this
// application's own destinations: "?view=health", "?view=settings#tab=workspace".
export function isExternalHref(href: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/iu.test(href) || href.startsWith('//');
}

// The router's resync event. Named here rather than imported so the state
// family does not depend on the router; the same string is already the shared
// signal in router/useView.ts and views/auth/authParams.ts.
const NAVIGATE_EVENT = 'aleph:navigate';

// Next actions are the real Button primitive (secondary sm), so a Button
// change propagates here instead of drifting (redo-1 P2).
//
// Every href action used to carry target="_blank". Measured on 2026-08-20:
// clicking "Open Provider Health" on Coach took the tab count from 1 to 2 and
// booted the whole application a second time, leaving the original tab on the
// state the reader was trying to act on. Twenty-six of the twenty-eight href
// actions in the product are internal, so that was the normal case, not the
// exception. Internal ones now move this tab, through the router's own
// pushState so the destination opens without a reload. Modified clicks and
// middle clicks keep the browser's own meaning.
function ActionControl({ action }: { readonly action: HonestAction }) {
  const href = action.href;
  if (href !== undefined) {
    if (isExternalHref(href)) {
      return (
        <ButtonLink size="sm" href={href} target="_blank" rel="noreferrer noopener" keyCap={action.keyHint}>
          {action.label}
        </ButtonLink>
      );
    }
    const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      window.history.pushState(window.history.state, '', href);
      window.dispatchEvent(new Event(NAVIGATE_EVENT));
      action.onSelect?.();
    };
    return (
      <ButtonLink size="sm" href={href} keyCap={action.keyHint} onClick={onClick}>
        {action.label}
      </ButtonLink>
    );
  }
  return (
    <Button size="sm" keyCap={action.keyHint} onClick={action.onSelect}>
      {action.label}
    </Button>
  );
}

export function HonestState(props: HonestStateProps) {
  const { variant, reason, nextAction, as = 'block', title, detail, className, id } = props;
  const problems = validateHonestStateProps({ variant, reason, nextAction });
  for (const problem of problems) warnInDev(problem);

  const tone = toneOf(variant);
  const actions = toActions(nextAction);
  const actionSummary = actions.map((action) => action.label).join(', ');

  if (as === 'pill') {
    return (
      <span
        id={id}
        className={['hs-pill', className].filter(Boolean).join(' ')}
        data-state={variant}
        data-tone={tone}
        title={`${reason} Next: ${actionSummary}.`}
        aria-label={`${variant}. ${reason} Next: ${actionSummary}.`}
      >
        <span className="hs-dot" aria-hidden="true" />
        {variant}
      </span>
    );
  }

  return (
    <div
      id={id}
      className={['hs-block', className].filter(Boolean).join(' ')}
      data-state={variant}
      data-tone={tone}
      role="status"
    >
      <p className="hs-title">
        <span className="hs-dot" aria-hidden="true" />
        <b>{title ?? variant}</b>
      </p>
      <p className="hs-reason">{reason}</p>
      {detail ? <div className="hs-detail">{detail}</div> : null}
      <div className="hs-next">
        {actions.map((action) => (
          <ActionControl key={`${action.label}:${action.href ?? ''}`} action={action} />
        ))}
      </div>
    </div>
  );
}
